import { Dispatch, SetStateAction, useEffect } from "react";
import {
  Modal,
  TextField,
  Button,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import { showError, showToast } from "../constants/toasts";
import {
  useAddSubCategoryMutation,
  useUpdateSubCategoryMutation,
  useLazyGetSubCategoryByIdQuery,
  useLazyGetCategoryQuery,
} from "../services/shopingMeal";

interface Props {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  categoryId: number | string;
  editId?: number | string | null;
  isEdit?: boolean;
  onSuccess?: () => void;
}

export default function AddShoppingSubCategory({
  open,
  onClose,
  setOpen,
  categoryId,
  editId = null,
  isEdit = false,
  onSuccess,
}: Props) {
  const [subCategoryName, setSubCategoryName] = React.useState<string>("");
  const [expiryTime, setExpiryTime] = React.useState<string>("");
  const [error, setError] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<string | number>("");
  const [categoryOptions, setCategoryOptions] = React.useState<Array<{ id: number; name: string }>>([]);

  const [addSubCategory] = useAddSubCategoryMutation();
  const [updateSubCategory] = useUpdateSubCategoryMutation();
  const [getSubCategoryById] = useLazyGetSubCategoryByIdQuery();
  const [getCategories] = useLazyGetCategoryQuery();

  useEffect(() => {
    const init = async () => {
      if (open && isEdit && editId) {
        try {
          const idNum = typeof editId === "string" ? parseInt(editId, 10) : editId;
          if (!idNum || Number.isNaN(idNum)) return;
          const response = await getSubCategoryById({ id: idNum as number }).unwrap();
          const data = response?.data || {};
          const name = data?.name || data?.subcategory_name || "";
          const expiry = data?.expiry_date || data?.expiry_time || data?.expiry || "";
          setSubCategoryName(name);
          setExpiryTime(expiry);
          const derivedCategory = (data?.category && (data?.category?.id || data?.category?._id || data?.category)) || data?.category_id;
          if (derivedCategory) setSelectedCategoryId(derivedCategory);
        } catch (e: any) {
          showError(e?.data?.message || "Failed to load sub-category");
        }
      } else if (open && !isEdit) {
        setSubCategoryName("");
        setExpiryTime("");
        if (categoryId) setSelectedCategoryId(categoryId);
      }
    };
    init();
  }, [open, isEdit, editId, getSubCategoryById]);

  useEffect(() => {
    const loadCategories = async () => {
      if (!open) return;
      try {
        const resp = await getCategories({ length: 100, start: 1, search: "" }).unwrap();
        const list = resp?.data?.response_object || [];
        setCategoryOptions(list.map((it: any) => ({ id: it.id ?? it._id, name: it.name })));
        if (!isEdit && categoryId) setSelectedCategoryId(categoryId);
      } catch (e: any) {
        console.log(e);
      }
    };
    loadCategories();
  }, [open, getCategories, isEdit, categoryId]);

  const handleSubmit = async () => {
    setError(true);
    const trimmedName = subCategoryName?.trim() ?? "";
    if (!trimmedName.length) {
      showError("Please input sub-category name");
      return;
    }
    const normalizedCategoryId = typeof selectedCategoryId === "string" ? parseInt(selectedCategoryId, 10) : selectedCategoryId;
    if (!normalizedCategoryId || Number.isNaN(normalizedCategoryId as number)) {
      showError("Please select a category");
      return;
    }
    setIsLoading(true);
    try {
      let response: any;
      const body: any = { name: trimmedName, category: normalizedCategoryId };
      if ((expiryTime || "").trim()) body["expiry_date"] = expiryTime.trim();
      if (isEdit) {
        const idNum = isEdit && editId ? (typeof editId === "string" ? parseInt(editId, 10) : editId) : undefined;
        response = await updateSubCategory({ body, id: idNum as number }).unwrap();
      } else {
        response = await addSubCategory({ body }).unwrap();
      }
      if (response?.status === 200 || response?.status === 201 || response?.statusCode === 200) {
        setSubCategoryName("");
        setExpiryTime("");
        setSelectedCategoryId("");
        setOpen(false);
        onSuccess?.();
        showToast(response?.message || (isEdit ? "Sub-category updated successfully" : "Sub-category added successfully"));
      }
    } catch (err: any) {
      showError(err?.data?.message || "Something went wrong");
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal className="modal alertConfig_modal" open={open} onClose={onClose}>
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>Add Shopping Sub-Category</h2>
            <div className="btn-close" onClick={() => setOpen(false)}>
              <CloseIcon />
            </div>
          </div>

          <form className="form" action="">
            <div className="control_group">
              <label>Select Category</label>
              <Select
                fullWidth
                value={selectedCategoryId || ""}
                onChange={(e: SelectChangeEvent<any>) => setSelectedCategoryId(e.target.value)}
                displayEmpty
                disabled={!isEdit}
              >
                <MenuItem disabled value="">
                  Select Category
                </MenuItem>
                {categoryOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
              {error && !selectedCategoryId ? (
                <h6 className="err_msg">Category is required</h6>
              ) : (
                ""
              )}
            </div>
            <div className="control_group">
              <label> Sub-Category Name</label>
              <TextField
                fullWidth
                hiddenLabel
                placeholder="Enter"
                value={subCategoryName}
                onChange={(e) => setSubCategoryName(e.target.value)}
              />
              {error && !subCategoryName.trim() ? (
                <h6 className="err_msg">Sub-category name is required</h6>
              ) : (
                ""
              )}
            </div>
            <div className="control_group">
              <label>Expiry Time</label>
              <TextField
                fullWidth
                hiddenLabel
                placeholder="Enter"
                value={expiryTime}
                onChange={(e) => setExpiryTime(e.target.value)}
              />
            </div>
            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                onClick={() => {
                  setError(false);
                  setOpen(false);
                  setSubCategoryName("");
                  setExpiryTime("");
                  setSelectedCategoryId("");
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                className="btn btn_primary"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : isEdit ? "Update" : "Submit"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
